import { Cookie } from '@libs/common-next';
import type { AlgoliaClickEvent } from '../types/algoliaTracking';
import type { Product } from '@app/pdp/types';
import { type MappedProductVariant, type ProductHit } from '@libs/algolia';
const COOKIE_EXPIRY_HOURS = 1 / 24; // 1 hour
const MINIMUM_CONSENT_GROUPS = 4;
declare global {
    interface Window {
        OnetrustActiveGroups?: string;
    }
}

export const getPDPEventData = (product: Product | ProductHit | MappedProductVariant): AlgoliaClickEvent | null => {
    let objectIDs: string[] | undefined;

    // Check for productVariants (Product type)
    if ('productVariants' in product && product.productVariants?.variants) {
        objectIDs = product.productVariants.variants.map((variant: { objectID: string }) => variant.objectID);
    }
    // Fallback for ProductHit type (assume objectID exists)
    else if ('objectID' in product) {
        objectIDs = [product.objectID];
    }

    const algoliaCookie = getAlgoliaEvents();
    if (algoliaCookie && objectIDs) {
        for (const event of algoliaCookie) {
            if (objectIDs.some((id: string) => event.objectIDs.includes(id))) {
                return event;
            }
        }
    }
    return null;
};

export const deleteEvents = () => {
    Cookie.remove('algoliaEvents');
};

export const getAlgoliaEvents = () => {
    const algoliaCookie = Cookie.get('algoliaEvents');
    if (algoliaCookie) {
        return JSON.parse(algoliaCookie) as AlgoliaClickEvent[];
    }
    return [] as AlgoliaClickEvent[];
};

export const removeProductAlgoliaEvent = (objectId: string) => {
    const algoliaEvents = getAlgoliaEvents();

    const updatedEvents = algoliaEvents
        .map(event => ({
            ...event,
            objectIDs: event.objectIDs.filter(id => id !== objectId),
        }))
        .filter(event => event.objectIDs.length > 0);
    Cookie.set('algoliaEvents', JSON.stringify(updatedEvents));
};

export const addUpdateAlgoliaEvents = (queryId: string, objectId: string, addedToCart: boolean) => {
    let localSearchEvents = getAlgoliaEvents();
    const search = localSearchEvents.filter(clickInfo => clickInfo.queryID === queryId);
    if (search.length !== 0) {
        if (!search[0].objectIDs.includes(objectId)) {
            search[0].objectIDs.push(objectId);
        }
        search[0].addedToCart = addedToCart;
        const searchClicks = localSearchEvents.filter(clickInfo => clickInfo.queryID !== queryId);
        searchClicks.push(search[0]);
        localSearchEvents = searchClicks;
    } else {
        const clickInfo = {
            objectIDs: [objectId],
            queryID: queryId,
            addedToCart,
        } as AlgoliaClickEvent;
        localSearchEvents.push(clickInfo);
    }

    Cookie.set('algoliaEvents', JSON.stringify(localSearchEvents), { expires: COOKIE_EXPIRY_HOURS });
};

export const isOptedOutTracking = () => {
    const activeGroups = window.OnetrustActiveGroups?.split(',');
    return !activeGroups || activeGroups.length < MINIMUM_CONSENT_GROUPS;
};
